import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * TerminologyContext — the live OS-config layer of the Workspace Builder
 * (owner-locked 2026-07). Loads the company's approved terminology, dashboard
 * widget layout and scheduling mode ONCE and exposes them to every surface.
 * Empty config = the OS looks and behaves exactly as before (zero change
 * until the owner approves a blueprint).
 */
const TerminologyContext = createContext({
  terms: {}, widgets: {}, scheduling: {}, appearance: {}, landing: null, refresh: () => {},
});

const token = () => localStorage.getItem('token')
  || localStorage.getItem('auth_token') || localStorage.getItem('access_token') || '';

export const TerminologyProvider = ({ children }) => {
  const [config, setConfig] = useState({ terms: {}, widgets: {}, scheduling: {}, appearance: {}, landing: null });
  const loadedFor = useRef('');
  const location = useLocation();

  const refresh = useCallback(async (force = false) => {
    const companyId = localStorage.getItem('company_id');
    const tok = token();
    if (!companyId || !tok) return;
    if (!force && loadedFor.current === companyId) return;
    try {
      const { data } = await axios.get(
        `${API}/api/ari/workspace-builder/${companyId}/os-config`,
        { headers: { Authorization: `Bearer ${tok}` } });
      loadedFor.current = companyId;
      setConfig({
        terms: data.terminology || {},
        widgets: data.dashboard_widgets || {},
        scheduling: data.scheduling_mode || {},
        appearance: data.appearance || {},
        landing: data.default_landing || null,
      });
    } catch { /* keep defaults */ }
  }, []);

  // MYOS §7 — accent colors exposed as CSS variables. Empty = zero change.
  useEffect(() => {
    const a = config.appearance || {};
    const root = document.documentElement;
    if (a.accent) root.style.setProperty('--myos-accent', a.accent);
    else root.style.removeProperty('--myos-accent');
    if (a.secondary) root.style.setProperty('--myos-secondary', a.secondary);
    else root.style.removeProperty('--myos-secondary');
  }, [config.appearance]);

  // Re-check on every route change — covers SPA login (token/company_id only
  // exist AFTER navigation). loadedFor guard = one fetch per company.
  useEffect(() => { refresh(); }, [refresh, location.pathname]);

  useEffect(() => {
    const onChanged = () => refresh(true);
    window.addEventListener('workspace:os-config-changed', onChanged);
    return () => window.removeEventListener('workspace:os-config-changed', onChanged);
  }, [refresh]);

  return (
    <TerminologyContext.Provider value={{ ...config, refresh }}>
      {children}
    </TerminologyContext.Provider>
  );
};

export const useOsConfig = () => useContext(TerminologyContext);

const matchCase = (word, fallback) => {
  if (!word) return fallback;
  if (fallback && /^[A-Z]/.test(fallback)) {
    return word.replace(/(^|\s)\w/g, (c) => c.toUpperCase());
  }
  return word;
};

/** useTerm()('jobs', 'Jobs') → 'Validations' when configured, else 'Jobs'. */
export const useTerm = () => {
  const { terms } = useContext(TerminologyContext);
  return useCallback((key, fallback) => matchCase(terms?.[key], fallback), [terms]);
};
